import type { SetDefaultInput } from './flags';
import type { FeatureFlagDefault } from '../schemas/flag';

/**
 * Build a `PUT /flags/:key/default` body that changes one field and preserves
 * the rest.
 *
 * ## Why this is not an inline object at each call site
 *
 * `PUT /default` **replaces** the row. Every field the caller omits is therefore
 * a field it has cleared, not a field it has left alone — and the one that
 * matters is `rolloutPercentage`, where absent means "no gate" rather than
 * "unchanged". A toggle that sent `{ enabled, expectedVersion }` and nothing
 * else would take a flag gated at 25% and serve it to **every** museum without
 * an override, in a single click, with no error anywhere: the write is valid,
 * the audit entry is truthful, and nothing downstream can tell it apart from an
 * operator who meant to go to 100%.
 *
 * That is the worst failure available on this screen, so building the body is a
 * named, tested function rather than an object literal that has to be got right
 * five separate times.
 *
 * Lives in `api/` because it constructs a request body — it is the shape of the
 * call, not state the UI holds.
 */
export function defaultWriteFrom(
    current: FeatureFlagDefault,
    change: { enabled?: boolean; rolloutPercentage?: number; archived?: boolean },
    reason?: string,
): SetDefaultInput {
    const rolloutPercentage = change.rolloutPercentage ?? current.rolloutPercentage;

    return {
        enabled: change.enabled ?? current.enabled,
        // `archived` is echoed rather than omitted for the same reason: the API
        // preserves it when absent, but relying on that would make this body
        // mean different things depending on which endpoint received it.
        archived: change.archived ?? current.archived,
        // Always the version the screen was rendered from. This is what turns a
        // colleague's concurrent change into a 409 the operator can see instead
        // of a silent overwrite.
        expectedVersion: current.version,
        ...(rolloutPercentage !== undefined ? { rolloutPercentage } : {}),
        ...(reason !== undefined ? { reason } : {}),
    };
}
